import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  TextInput,
} from "@mantine/core";
import {
  useUsers,
  useUserOffboard,
  useUserUpdate,
  useUserInviteSend,
} from "hooks/useUser";
import HeaderTitle from "components/ui/Header/HeaderTitle";
import ModalButtons from "components/ui/Modal/ModalButtons";
import { Filters, Prompts, UserData } from "types/module/User";
import defaults from "config/forms.json";
import BannerBottom from "pages/User/BannerBottom";
import UsersTable from "pages/User/Table";
import Filter from "pages/User/Filter";

const defaultPromptState = {
  offboard: false,
  profileChange: false,
  statusChange: false,
  resendInvite: false,
  invalid: false,
};

const User: React.FC = () => {
  document.title = "Manage Users";

  const {
    data: userData,
    isLoading: isUserDataLoading,
    isSuccess: isUserDataSuccess,
    refetch: userDataRefetch,
  } = useUsers();

  const {
    mutate: userOffboardMutate,
    isSuccess: userOffboardSuccess,
    isError: userOffboardError,
  } = useUserOffboard();

  const {
    mutate: userUpdateMutate,
    isSuccess: userUpdateSuccess,
    isError: userUpdateError,
  } = useUserUpdate();

  const {
    mutate: userInviteSendMutate,
    isSuccess: userInviteSendSuccess,
    isError: userInviteSendError,
  } = useUserInviteSend(true);

  const [users, setUsers] = useState<UserData[]>([]);
  const [isOnlyAdmin, setIsOnlyAdmin] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserData>(null);
  const [offboardReason, setOffboardReason] = useState<string>("");
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isPromptOpen, setIsPromptOpen] =
    useState<Prompts>(defaultPromptState);

  const [filters, setFilters] = useState<Filters>({
    status: "",
    isInviting: false,
  });

  const handleOpenOffboardPrompt = (user: UserData) => {
    if (
      isOnlyAdmin &&
      user.profile == "Admin" &&
      user.isActive &&
      users.length > 1
    ) {
      setIsPromptOpen((prev) => ({ ...prev, invalid: true }));
    } else {
      setSelectedUser(user);
      setIsPromptOpen((prev) => ({ ...prev, offboard: true }));
    }
  };

  const handleOffboard = (user: UserData) => {
    setIsSubmitting(true);
    setIsPromptOpen((prev) => ({ ...prev, offboard: false }));

    userOffboardMutate({
      userId: user.Id,
      name: user.name,
      email: user.email,
      reason: offboardReason,
    });
  };

  const handleOpenProfileChangePrompt = (user: UserData, profile: string) => {
    if (
      isOnlyAdmin &&
      user.profile == "Admin" &&
      user.isActive &&
      users.length > 1
    ) {
      setIsPromptOpen((prev) => ({ ...prev, invalid: true }));
    } else {
      setSelectedUser(user);

      if (profile !== "Admin") {
        if (userData?.currentUser?.Id == user.Id) {
          setIsPromptOpen((prev) => ({ ...prev, profileChange: true }));
          setSelectedProfile(profile);
        } else {
          handleProfileChange(user, profile);
        }
      } else {
        setIsPromptOpen((prev) => ({ ...prev, profileChange: true }));
      }
    }
  };

  const handleProfileChange = (user: UserData, profile = "Admin") => {
    setIsSubmitting(true);
    setIsPromptOpen((prev) => ({ ...prev, profileChange: false }));
    setSelectedProfile(null);

    userUpdateMutate({
      userId: user.Id,
      name: user.name,
      email: user.email,
      profile: profile,
    });
  };

  const handleOpenStatusChangePrompt = (user: UserData) => {
    if (
      isOnlyAdmin &&
      user.profile == "Admin" &&
      user.isActive &&
      users.length > 1
    ) {
      setIsPromptOpen((prev) => ({ ...prev, invalid: true }));
    } else {
      setSelectedUser(user);
      if (userData?.currentUser?.Id == user.Id) {
        setIsPromptOpen((prev) => ({ ...prev, statusChange: true }));
      } else {
        handleStatusChange(user);
      }
    }
  };

  const handleStatusChange = (user: UserData) => {
    setIsSubmitting(true);
    setIsPromptOpen((prev) => ({ ...prev, statusChange: false }));

    userUpdateMutate({
      userId: user.Id,
      name: user.name,
      email: user.email,
      isActive: !user.isActive,
    });
  };

  const handleResendInvite = (user: UserData) => {
    setIsSubmitting(true);
    setIsPromptOpen((prev) => ({ ...prev, resendInvite: false }));

    userInviteSendMutate({
      email: user.email,
      name: user.name,
      invitationId: user.invite_id,
    });
  };

  const handleClosePrompt = () => {
    setSelectedUser(null);
    setOffboardReason("");
    setSelectedProfile(null);

    setIsPromptOpen(defaultPromptState);
  };

  useEffect(() => {
    if (isUserDataSuccess) {
      setUsers(userData?.userList);

      setIsOnlyAdmin(
        userData?.userList?.filter(
          (item: UserData) => item.profile == "Admin" && item.isActive
        ).length <= 1
      );
    }
  }, [isUserDataSuccess, userData]);

  useEffect(() => {
    if (
      userOffboardSuccess ||
      userOffboardError ||
      userUpdateSuccess ||
      userUpdateError ||
      userInviteSendSuccess ||
      userInviteSendError
    ) {
      userDataRefetch().then(() => {
        setIsSubmitting(false);
        handleClosePrompt();
      });
    }
  }, [
    userOffboardSuccess,
    userOffboardError,
    userUpdateSuccess,
    userUpdateError,
    userInviteSendSuccess,
    userInviteSendSuccess,
  ]);

  return (
    <>
      <HeaderTitle title="Manage Users" />
      <Filter filters={filters} setFilters={setFilters} />
      <UsersTable
        users={users}
        setSelectedUser={setSelectedUser}
        isUserDataLoading={isUserDataLoading}
        setIsPromptOpen={setIsPromptOpen}
        filters={filters}
        isSubmitting={isSubmitting}
        handleOpenProfileChangePrompt={handleOpenProfileChangePrompt}
        handleOpenStatusChangePrompt={handleOpenStatusChangePrompt}
        handleOpenOffboardPrompt={handleOpenOffboardPrompt}
      />
      <Modal
        title={"Are you sure?"}
        opened={isPromptOpen.offboard}
        onClose={handleClosePrompt}
        centered
      >
        This will remove <b>{selectedUser?.name}&apos;s</b> access to the DNA
        portal entirely. To re-enable access for <b>{selectedUser?.name}</b> you
        will have to go through the registration process again. Are you happy to
        proceed?
        <TextInput
          key="reason"
          placeholder="Enter Reason"
          disabled={isSubmitting}
          mt="md"
          value={offboardReason}
          maxLength={defaults.input.maxLength.default}
          onChange={(e) => setOffboardReason(e.target.value)}
        />
        <ModalButtons>
          <Button variant="default" onClick={handleClosePrompt}>
            Cancel
          </Button>
          <Button color="red" onClick={() => handleOffboard(selectedUser)}>
            Remove
          </Button>
        </ModalButtons>
      </Modal>
      <Modal
        title="Are you sure?"
        opened={isPromptOpen.profileChange}
        onClose={handleClosePrompt}
        centered
      >
        {userData?.currentUser?.Id == selectedUser?.Id && (
          <>
            Are you sure you want to change your own account to{" "}
            <b>{selectedProfile}</b> Profile?
          </>
        )}
        {userData?.currentUser?.Id != selectedUser?.Id && (
          <>
            Changing <b>{selectedUser?.name}&apos;s</b> profile to Admin will
            allow them to administer all other users of the portal and provide
            access to firmware and licensing tools. Are you happy to proceed?
          </>
        )}
        <ModalButtons>
          <Button variant="default" onClick={handleClosePrompt}>
            Cancel
          </Button>
          <Button onClick={() => handleProfileChange(selectedUser)}>
            Change
          </Button>
        </ModalButtons>
      </Modal>
      <Modal
        title="Are you sure?"
        opened={isPromptOpen.statusChange}
        onClose={handleClosePrompt}
        centered
      >
        Are you sure you want to de-activate your own account?
        <ModalButtons>
          <Button variant="default" onClick={handleClosePrompt}>
            Cancel
          </Button>
          <Button color="red" onClick={() => handleStatusChange(selectedUser)}>
            Deactivate
          </Button>
        </ModalButtons>
      </Modal>
      <Modal
        title="At least one Portal Admin is required"
        opened={isPromptOpen.invalid}
        onClose={handleClosePrompt}
        centered
      >
        Please re-assign the portal administrator permission to an existing user
        before you remove yourself.
        <ModalButtons>
          <Button variant="default" onClick={handleClosePrompt}>
            OK
          </Button>
        </ModalButtons>
      </Modal>
      <Modal
        title="Are you sure you want to resend invite?"
        opened={isPromptOpen.resendInvite}
        onClose={handleClosePrompt}
        centered
      >
        <ModalButtons>
          <Button
            variant="filled"
            onClick={() => handleResendInvite(selectedUser)}
          >
            Resend Now
          </Button>
          <Button variant="default" onClick={handleClosePrompt}>
            Cancel
          </Button>
        </ModalButtons>
      </Modal>
      <BannerBottom />
    </>
  );
};

export default User;
